import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import GradientBar from '@/components/shared/GradientBar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  // Show sign-in errors passed back in the URL (e.g. a cancelled Google login)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorDesc = params.get('error_description') || params.get('error');
    if (errorDesc) {
      toast.error('Giriş hatası: ' + decodeURIComponent(errorDesc));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Redirect if already logged in as admin
  useEffect(() => {
    if (!authLoading && user && isAdmin) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [user, isAdmin, authLoading, navigate]);

  // Google sends the admin back to /auth/callback, which checks admin rights.
  const handleGoogleLogin = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) {
      console.error('Google login error:', error);
      toast.error('Google ile giriş başlatılamadı. Lütfen tekrar deneyin.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <GradientBar className="flex-1 flex flex-col px-10">
        {/* Logo */}
        <div className="mt-[190px] text-center">
          <h1 className="flex justify-center" data-testid="admin-login-logo">
            <img src="/brand/appstars-logo.png" alt="Appstars" className="h-14 w-auto" />
          </h1>
          <p className="font-brand text-[18px] text-brand-ink mt-4">
            Admin Panel
          </p>
        </div>

        {/* Login */}
        <div className="mt-[80px]">
          <p className="text-[15px] font-semibold text-brand-ink text-center mb-5">
            Devam etmek için giriş yapın
          </p>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full h-11 rounded-lg text-base font-semibold bg-brand-neon text-brand-ink border border-brand-ink hover:bg-brand-neon/85 gap-3"
            data-testid="admin-google-login-button"
          >
            <GoogleIcon />
            {loading ? 'Yönlendiriliyor...' : 'Google ile giriş yap'}
          </Button>
        </div>
      </GradientBar>
    </div>
  );
};

export default AdminLoginPage;
